import { OfferObjectAvailability } from '@/api/v2/requests/offers';
const toClientNameSet = (clientNames) => {
    if (!clientNames)
        return new Set();
    // `clientNames` comes as a string (often comma-separated).
    return new Set(String(clientNames)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean));
};
const monthKey = (year, month) => year * 100 + month;
const getStatusRank = (a) => {
    const first = a?.global?.firstHalfAvailability;
    const second = a?.global?.secondHalfAvailability;
    const has = (s) => first === s || second === s;
    // Priority for displayed "sale monthly price": contract > offer > available
    if (has(OfferObjectAvailability.IN_CONTRACT))
        return 3;
    if (has(OfferObjectAvailability.IN_OFFER))
        return 2;
    return 0;
};
const getPriceCandidate = (a) => {
    const localPrice = a?.local?.price;
    if (typeof localPrice === 'number' && Number.isFinite(localPrice)) {
        return localPrice;
    }
    const globalPrice = a?.global?.price;
    if (typeof globalPrice === 'number' && Number.isFinite(globalPrice)) {
        return globalPrice;
    }
    return null;
};
const matchesClient = (a, clientNameSet) => {
    if (clientNameSet.size === 0)
        return true;
    const name = a?.global?.clientName;
    if (!name)
        return false;
    return clientNameSet.has(String(name));
};
export const getEffectiveSaleMonthlyPrice = (object, opts) => {
    const availability = object.availability ?? [];
    const now = opts?.now ?? new Date();
    const nowKey = monthKey(now.getFullYear(), now.getMonth() + 1);
    // If we have no availability at all, the only thing we can reliably use is offerObjectPrices.
    if (!Array.isArray(availability) || availability.length === 0) {
        const offerPrice = object.offerObjectPrices?.saleMonthlyPrice;
        const ok = typeof offerPrice === 'number' && Number.isFinite(offerPrice);
        return ok ? offerPrice : null;
    }
    const clientNameSet = toClientNameSet(object.clientNames);
    // Aggregate per month (because API can contain duplicates for the same month).
    const perMonth = new Map();
    for (const a of availability) {
        if (!a?.global)
            continue;
        if (!matchesClient(a, clientNameSet))
            continue;
        const y = Number(a.year);
        const m = Number(a.month);
        if (!y || !m)
            continue;
        const k = monthKey(y, m);
        const rank = getStatusRank(a);
        const price = getPriceCandidate(a);
        const row = perMonth.get(k) ?? {
            contractPrice: null,
            offerPrice: null,
        };
        if (rank === 3) {
            if (price !== null) {
                row.contractPrice =
                    row.contractPrice === null
                        ? price
                        : Math.max(row.contractPrice, price);
            }
        }
        else if (rank === 2) {
            if (price !== null) {
                row.offerPrice =
                    row.offerPrice === null ? price : Math.max(row.offerPrice, price);
            }
        }
        perMonth.set(k, row);
    }
    // 1) Active contract (current month)
    const activeContract = perMonth.get(nowKey)?.contractPrice ?? null;
    if (activeContract !== null)
        return activeContract;
    // 2) Last contract (latest past month with a contract price)
    let lastContractKey = null;
    let lastContractPrice = null;
    for (const [k, v] of perMonth.entries()) {
        if (k >= nowKey)
            continue;
        if (v.contractPrice === null)
            continue;
        if (lastContractKey === null || k > lastContractKey) {
            lastContractKey = k;
            lastContractPrice = v.contractPrice;
        }
    }
    if (lastContractPrice !== null && lastContractKey !== null) {
        return lastContractPrice;
    }
    // 3) Current offer (offer-specific prices)
    const offerPrice = object.offerObjectPrices?.saleMonthlyPrice;
    if (typeof offerPrice === 'number' && Number.isFinite(offerPrice)) {
        return offerPrice;
    }
    // 4) Latest active offer (from availability, pick the latest month >= now)
    // This handles cases where there are multiple active offers on different periods:
    // we show the price from the offer with the latest time period.
    let latestActiveOfferKey = null;
    let latestActiveOfferPrice = null;
    for (const [k, v] of perMonth.entries()) {
        if (k < nowKey)
            continue;
        if (v.offerPrice === null)
            continue;
        if (latestActiveOfferKey === null || k > latestActiveOfferKey) {
            latestActiveOfferKey = k;
            latestActiveOfferPrice = v.offerPrice;
        }
    }
    if (latestActiveOfferPrice !== null)
        return latestActiveOfferPrice;
    return null;
};
export const formatEffectiveSaleMonthlyPrice = (object, opts) => {
    const price = getEffectiveSaleMonthlyPrice(object, opts);
    if (price === null)
        return '-';
    return `${price} PLN`;
};
